import * as PIXI from 'pixi.js';

import * as idb from 'idb-keyval';
import { Settings } from "../Settings";
import { hashName } from "../AssetList";
import {setVoiceMods} from './AudioManager';
import {setBloodColorTheme} from './BloodColors.js';
import {getItem, setItem, removeItem} from './LocalStorageWrapper';
import * as SaveManager from './SaveManager';
import {MOD_ATLASES} from '../../static/mod/modSchema.js';
import {readModState, replaceModFiles, saveModCache, CACHE_PREFIX, PREVIEW_KEY, mapLimit} from '../../static/mod/modStorage.js';
import {runModWorker} from '../../static/mod/modWorkerClient.js';
import {ModAtlasBuilder, decodeModImage, makeModCanvas} from './ModAtlasBuilder.js';
import assetVersion from 'virtual:mod-asset-version';

const folderName = 'jollymod';

const moddedTextures = [];

export const getModdedPortrait = async (name, fallback) => {
	const saveKey = `${folderName}/portraits/${name}`;

	try{
		const blob = await idb.get(saveKey);

		if(blob){
			const url = URL.createObjectURL(blob);
			return url;
		}
	}catch(err){
	};

	return `${fallback}${hashName(name)}`;
}

export const getCustomModdedPortrait = async (name, fallback) => {
	try {
		const state = await readModState(idb);
		const skin = Number(/characterselect(\d+)/.exec(name)?.[1]) - 1;
		const [cached, legacyPortrait] = await idb.getMany([`${CACHE_PREFIX}preview/${skin}`, PREVIEW_KEY]);
		if (cached?.revision === state.revision && cached.assetVersion === assetVersion && cached.portraitBlob instanceof Blob) {
			return URL.createObjectURL(cached.portraitBlob);
		}
		// Existing installations keep their menu portrait until the picker makes
		// a new one. Replacing or removing a mod deletes this legacy entry.
		if (state.keys.length && legacyPortrait instanceof Blob) return URL.createObjectURL(legacyPortrait);
	} catch { /* Fall back to a supplied or built-in portrait. */ }
	return getModdedPortrait(name, fallback);
};

let modRevision = 0;
let voiceRevision = 0;
let appliedRevision = null;
let loadedVoiceRevision = null;
let currentWork = null;
const CACHE_VERSION = 1;

export const refreshVoiceMods = async (state) => {
	const revision = ++voiceRevision;
	try {
		state ||= await readModState(idb);
		if (revision !== voiceRevision || loadedVoiceRevision === state.revision) return;
		setVoiceMods(state.keys);
		loadedVoiceRevision = state.revision;
	} catch {
		if (revision === voiceRevision) { setVoiceMods([]); loadedVoiceRevision = null; }
	}
};

const modId = name => (name || '').toLowerCase().replace(/[^a-z0-9]/g, '');

export const installLinkedMod = async () => {
	const params = new URLSearchParams(window.location.search);
	const name = params.get('mod') || params.get('gd-mod');
	const id = modId(name);
	if (!id || [getItem('jollyModName'), getItem('jollyModNameFailed')].some(stored => modId(stored) === id)) return;
	const response = await fetch(`mod/zips/${id}.zip`).catch(() => null);
	if (!response) return;
	try {
		if (!response.ok) throw new Error(`HTTP ${response.status}`);
		const mod = await runModWorker(new URL('mod/modZipWorker.js', document.baseURI), await response.blob(), {type: 'classic'});
		await replaceModFiles(idb, mod.entries, mod.sourcePaths);
		removeItem('jollyWorldTheme');
		if (mod.theme !== null) setItem('jollyWorldTheme', mod.theme);
		setItem('jollyModName', name);
		const userData = SaveManager.getLocalUserdata();
		userData.selectedCharacter = 0;
		userData.selectedMask = mod.mask;
		SaveManager.updateLocalUserData(userData);
		window.modLoadingDoors?.();
	} catch (error) {
		console.warn(`Could not install mod ${name}`, error);
		setItem('jollyModNameFailed', name);
	}
};

export const init = async () => {
	const start = performance.now();
	try {
		const generation = modRevision;
		const state = await readModState(idb);
		if (generation !== modRevision) return init();
		if (appliedRevision === state.revision) {
			performance.measure('jollymod:load', {start, detail: {skipped: true, cacheHits: 0, assembled: 0, sourceReads: 0}});
			return;
		}
		if (currentWork?.revision === state.revision) return currentWork.promise;
		cleanMods();
		setBloodColorTheme(getItem('jollyWorldTheme'));
		const work = {revision: state.revision, generation: modRevision, builder: new ModAtlasBuilder()};
		currentWork = work;
		work.promise = loadMod(state, work, start).finally(() => {
			work.builder.dispose();
			if (currentWork === work) currentWork = null;
		});
		return await work.promise;
	} catch (error) {
		if (error.name !== 'AbortError') console.warn('Could not load mod assets', error);
	}
};

async function loadMod(state, work, start) {
	await refreshVoiceMods(state);
	const alive = () => work.generation === modRevision;
	const stats = {skipped: false, cacheHits: 0, assembled: 0, sourceReads: 0, workerSheets: 0};
	const groups = new Map(), tiles = [];
	for (const key of state.keys) {
		if (!key.endsWith('.png')) continue;
		const [, folder, name] = key.split('/');
		let atlas;
		if (['characters', 'kids', 'babies', 'hats'].includes(folder)) atlas = 'Characters_1.json';
		else if (folder === 'vehicles') atlas = 'Vehicles_1.json';
		// Older installs may still hold gore chunks; the game tears those from the character art now.
		else if (folder === 'gore' && name.startsWith('Gore_')) atlas = 'Characters_Gore.json';
		else if (folder === 'masks') atlas = 'Masks.json';
		else if (folder === 'atlases' && Object.hasOwn(MOD_ATLASES, name)) atlas = `${name}.json`;
		else if (folder === 'textures') {
			const texture = Settings.textureNames[Number(/(\d{4})\.png$/.exec(key)?.[1])];
			if (texture) tiles.push({key, name: texture});
		}
		if (atlas) {
			if (!groups.has(atlas)) groups.set(atlas, []);
			groups.get(atlas).push(key);
		}
	}
	const atlasNames = [...groups.keys()];
	const cacheKeys = atlasNames.map(name => `${CACHE_PREFIX}atlas/${name}`);
	const cached = cacheKeys.length ? await idb.getMany(cacheKeys) : [];
	for (let index = 0; index < atlasNames.length && alive(); index++) {
		const name = atlasNames[index];
		const sheet = PIXI.Assets.get(name);
		if (!sheet?.textureSource?.resource) continue;
		const source = sheet.textureSource;
		const base = source.originalResource || source.resource;
		const record = cached[index];
		if (record?.version === CACHE_VERSION && record.revision === state.revision && record.assetVersion === assetVersion &&
			record.width === base.width && record.height === base.height && record.blob instanceof Blob) {
			try {
				const image = await decodeModImage(record.blob);
				try {
					if (alive()) {
						const canvas = makeModCanvas(base.width, base.height);
						canvas.getContext('2d').drawImage(image, 0, 0);
						applyModSource(source, canvas);
						stats.cacheHits++;
					}
				} finally { image.close?.(); }
				continue;
			} catch { /* A damaged cache can be rebuilt from the original mod files. */ }
		}
		const patches = groups.get(name).map(key => {
			const filename = key.split('/').pop();
			const frame = (sheet.data.frames[filename] || sheet.data.frames[filename.slice(0, -4)])?.frame;
			return {key, frame};
		}).filter(patch => patch.frame);
		const blobs = await idb.getMany(patches.map(patch => patch.key));
		stats.sourceReads += patches.length;
		if (!alive()) break;
		const result = await work.builder.compose({base, width: base.width, height: base.height,
			patches: patches.map((patch, index) => ({frame: patch.frame, blob: blobs[index]}))});
		if (!alive()) break;
		if (!result.changed) continue;
		applyModSource(source, result.canvas);
		stats.assembled++;
		if (result.worker) stats.workerSheets++;
		if (result.blob) {
			await saveModCache(idb, state.revision, cacheKeys[index], {version: CACHE_VERSION, revision: state.revision,
				assetVersion, width: base.width, height: base.height, blob: result.blob}).catch(() => {});
		}
	}
	// Standalone terrain tiles need no assembled cache: their stored PNG is
	// already the finished image. Read them together and bound decode memory.
	if (tiles.length && alive()) {
		const blobs = await idb.getMany(tiles.map(tile => tile.key));
		stats.sourceReads += tiles.length;
		await mapLimit(tiles, 4, async (tile, index) => {
			if (!alive() || !(blobs[index] instanceof Blob)) return;
			let image;
			try {
				image = await decodeModImage(blobs[index]);
				if (!alive()) return;
				const source = PIXI.Texture.from(tile.name).source;
				const base = source.originalResource || source.resource;
				const canvas = makeModCanvas(base.width, base.height);
				canvas.getContext('2d').drawImage(image, 0, 0, base.width, base.height);
				applyModSource(source, canvas);
			} catch { /* Keep the original tile if its replacement cannot be decoded. */ }
			finally { image?.close?.(); }
		});
	}
	if (alive()) appliedRevision = state.revision;
	performance.measure('jollymod:load', {start, detail: {...stats, cancelled: !alive()}});
}

function applyModSource(source, canvas) {
	if (!source.originalResource) {
		source.originalResource = source.resource;
		moddedTextures.push(source);
	}
	source.resource = canvas;
	source.update();
}

export const cleanMods = () => {
	modRevision++;
	voiceRevision++;
	appliedRevision = null;
	loadedVoiceRevision = null;
	currentWork?.builder.dispose();
	currentWork = null;
	setVoiceMods([]);
	setBloodColorTheme(null);
	moddedTextures.forEach(source => {
		source.resource = source.originalResource;
		source.update();
		delete source.originalResource;
	});
	moddedTextures.length = 0;
};
